import { ProjectUser } from "../../models/projectUser"
import {
    BlockUserRequest,
    CreateProjectFromCopyRequest,
    CreateProjectRequest,
    InviteUsersRequest,
    IUpdateLogoRequest,
    Project,
    UpdateProjectRequest
} from "../../models/project"
import { GetReferenceTypesRequest, GetReferenceTypesResponse } from "../../models/reference"
import {
    DiffUpdateSettingsResponse,
    GetBandwidthGroupsResponse,
    GetSettingsResponse,
    GetSettingsWithProjectResponse,
    GetUserInviteSettingsResponse,
    ProjectSettings
} from "../../models/projectSettings"
import { AxiosProgressEvent } from "axios"
import { apiInstance } from "../instances/apiInstance"
import { apiInstanceV1 } from "../instances/apiInstanceV1"
import { instanceConfig } from "../config"
import { queryApi } from "../api"
import { handleQueryApiError } from "../utility/error"
import { FETCH_PROJECT_SETTINGS_FAILED_MESSAGE } from "../../store/projects/constants"
import { actions } from "../../store/projects/slice"

const projectsController = {
    create: (request: CreateProjectRequest): Promise<Project> =>
        apiInstance.post("/projects", request, { timeout: 60000 }).then(response => response.data),
    update: (request: UpdateProjectRequest): Promise<Project> =>
        apiInstance.put("/projects", request).then(response => response.data),

    updateLogo: ({ logo, projectId, hugeSize }: IUpdateLogoRequest): Promise<Project> => {
        const formData = new FormData()
        if (logo) {
            formData.append("logo", logo.Content, logo.Name)
        }

        let baseUrl = `projects/${projectId}/logo`

        if (hugeSize) {
            baseUrl += "?big_logo=true"
        }

        return apiInstance.put(baseUrl, formData).then(response => response.data)
    },
    getProjectCreatingStatus: (projectId: string): Promise<boolean> =>
        apiInstance.get(`projects/${projectId}/still_creating`).then(response => response.data),
    getProjects: (): Promise<Project[]> => apiInstance.get("/projects").then(response => response.data),
    getDefaultProjectSettings: (): Promise<GetSettingsWithProjectResponse> =>
        apiInstance.get("/projects/default-project-with-settings").then(response => response.data),
    invite: (request: InviteUsersRequest): Promise<ProjectUser[]> =>
        apiInstance.post("/projects/invite", request).then(response => response.data),
    exists: (projectId: string): Promise<boolean> =>
        apiInstance.post(`projects/${projectId}/exists`).then(response => response.data),
    block: (request: BlockUserRequest): Promise<void> =>
        apiInstance.post("/projects/block", request).then(response => response.data),
    upload: (file: File, onUploadProgress: (progressEvent: AxiosProgressEvent) => void): Promise<string> => {
        const formData = new FormData()
        formData.append("projectArchive", file)
        return apiInstance
            .post(`/projects/upload`, formData, {
                timeout: 600000,
                onUploadProgress
            })
            .then(response => response.data)
    },
    createFromCopy: (request: CreateProjectFromCopyRequest): Promise<Project> =>
        apiInstance.post("/projects/create_from_copy", request, { timeout: 60000 }).then(response => response.data),
    getExportUrl: (projectId: string, addDialogHistory: boolean): string => {
        return `${instanceConfig.baseURL}/projects/${projectId}/export?addDialogHistory=${addDialogHistory}`
    },
    getReferenceTypes: (request: GetReferenceTypesRequest): Promise<GetReferenceTypesResponse> =>
        apiInstanceV1.post("/reference/select_strict", request).then(response => response.data),
    getReferenceValues: (request: GetReferenceTypesRequest): Promise<GetReferenceTypesResponse> =>
        apiInstanceV1.post("/reference/select", request).then(response => response.data),
    updateSettings: (projectId: string, settings: ProjectSettings): Promise<DiffUpdateSettingsResponse> =>
        apiInstance.put(`/projects/${projectId}/settings`, settings).then(response => response.data),
    getBandwidthGroups: (): Promise<GetBandwidthGroupsResponse> =>
        apiInstance.get(`/projects/operators/bandwidth-settings-group`).then(response => response.data),
    getUserInviteSettings: (): Promise<GetUserInviteSettingsResponse> =>
        apiInstance.get(`user_invite_settings`).then(response => response.data)
}

export default projectsController

// TODO: Дубликат эндпоинта выше, надо переписать все API на RTK Query
export const projectsApi = queryApi.injectEndpoints({
    endpoints(build) {
        return {
            getSettings: build.query<GetSettingsResponse, { projectId: string }>({
                query: ({ projectId }) => ({
                    url: `/projects/${projectId}/settings`
                }),
                async onQueryStarted(_, { dispatch, queryFulfilled }) {
                    try {
                        const {
                            data: { Settings }
                        } = await queryFulfilled
                        dispatch(actions.setSettings(Settings))
                    } catch (e) {
                        handleQueryApiError(dispatch, FETCH_PROJECT_SETTINGS_FAILED_MESSAGE, e)
                    }
                }
            })
        }
    }
})

export const selectProjectSettings = (projectId: string) => projectsApi.endpoints.getSettings.select({ projectId })
export const { useGetSettingsQuery } = projectsApi
